import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel,
    IconButton, MenuItem, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import httpClient, { extractErrorMessage } from '../Services/httpClient';
import CoinReasonsManager from './CoinReasonsManager';
import CoinGrantPendingPanel from './CoinGrantPendingPanel';
import CoinStatementButton from './CoinStatementButton';

const today = () => new Date().toISOString().slice(0, 10);
const newBatchRow = () => ({ key: crypto.randomUUID(), consultant: null, quantity: '', description: '' });

// Autocomplete de pessoa via /backoffice/people/search?role= (endpoint já existia, criado
// originalmente pra atribuição de códigos promocionais, reaproveitado aqui). O backend
// (core-wallet CreateCoinGrant) só enxerga um ownerId de carteira, sem distinção de papel — a
// restrição de só-consultor no lote é uma escolha de produto (ajuste pedido pelo usuário: individual
// vale pra consultor ou talento, lote continua só consultor).
const PersonAutocomplete = ({ value, onChange, disabled, role, label }) => {
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!inputValue || inputValue.length < 2) {
            setOptions([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await httpClient.get('/backoffice/people/search', { params: { q: inputValue, role } });
                setOptions(response.data);
            } catch {
                setOptions([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [inputValue, role]);

    return (
        <Autocomplete
            disabled={disabled}
            options={options}
            loading={isSearching}
            value={value}
            onChange={(_, newValue) => onChange(newValue)}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isSearching && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
};

const ROLE_LABELS = { Consultant: 'Consultor', Talent: 'Talento' };

const CoinGrantsPanel = () => {
    const [mode, setMode] = useState('single');
    const [reasons, setReasons] = useState([]);
    const [reasonCode, setReasonCode] = useState('');
    const [description, setDescription] = useState('');
    const [referenceDate, setReferenceDate] = useState(today());
    const [notifyConsultant, setNotifyConsultant] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const pendingPanelRef = useRef(null);

    const [personRole, setPersonRole] = useState('Consultant');
    const [consultant, setConsultant] = useState(null);
    const [quantity, setQuantity] = useState('');

    const [batchRows, setBatchRows] = useState([newBatchRow()]);

    const activeReasons = reasons.filter((r) => r.active);

    const resetResult = () => {
        setError(null);
        setResult(null);
    };

    const handleModeChange = (_, newMode) => {
        if (newMode) {
            setMode(newMode);
            resetResult();
        }
    };

    const handlePersonRoleChange = (_, newRole) => {
        if (newRole) {
            setPersonRole(newRole);
            setConsultant(null);
        }
    };

    const validateCommon = () => {
        if (!reasonCode) return 'Selecione um motivo.';
        if (description.trim().length < 10) return 'A justificativa deve ter pelo menos 10 caracteres.';
        return null;
    };

    const handleSubmitSingle = async () => {
        resetResult();
        const commonError = validateCommon();
        if (commonError) { setError(commonError); return; }
        if (!consultant) { setError(`Selecione um ${ROLE_LABELS[personRole].toLowerCase()}.`); return; }
        if (!quantity || Number(quantity) <= 0) { setError('Informe uma quantidade positiva.'); return; }

        setIsSubmitting(true);
        try {
            const response = await httpClient.post(`/backoffice/consultants/${consultant.id}/coin-grants`, {
                quantity: Number(quantity),
                reasonCode,
                description: description.trim(),
                referenceDate,
                idempotencyKey: crypto.randomUUID(),
                notifyConsultant
            });
            setResult(response.data);
            setConsultant(null);
            setQuantity('');
            if (response.data.status === 'PendingApproval') pendingPanelRef.current?.reload();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddBatchRow = () => setBatchRows((rows) => [...rows, newBatchRow()]);
    const handleRemoveBatchRow = (key) => setBatchRows((rows) => rows.filter((r) => r.key !== key));
    const handleBatchRowChange = (key, field, value) =>
        setBatchRows((rows) => rows.map((r) => (r.key === key ? { ...r, [field]: value } : r)));

    const handleSubmitBatch = async () => {
        resetResult();
        const commonError = validateCommon();
        if (commonError) { setError(commonError); return; }
        const validRows = batchRows.filter((r) => r.consultant && Number(r.quantity) > 0);
        if (validRows.length === 0) { setError('Adicione ao menos um consultor com quantidade positiva.'); return; }

        setIsSubmitting(true);
        try {
            const response = await httpClient.post('/backoffice/coin-grants/batch', {
                items: validRows.map((r) => ({
                    consultantId: r.consultant.id,
                    quantity: Number(r.quantity),
                    description: r.description.trim() || null
                })),
                reasonCode,
                description: description.trim(),
                referenceDate,
                source: 'ManualSelection',
                notifyConsultant
            });
            setResult(response.data);
            setBatchRows([newBatchRow()]);
            if (response.data.results.some((r) => r.success && !r.transactionId)) pendingPanelRef.current?.reload();
        } catch (err) {
            setError(extractErrorMessage(err));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px', mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ color: '#f77513', fontWeight: 'bold' }}>
                    Atribuição de Lemon Coins
                </Typography>
                <CoinStatementButton />
            </Box>

            <CoinReasonsManager onReasonsChange={setReasons} />

            <Divider sx={{ my: 2 }} />

            <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small" sx={{ mb: 2 }}>
                <ToggleButton value="single">Individual</ToggleButton>
                <ToggleButton value="batch">Lote</ToggleButton>
            </ToggleButtonGroup>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {result && mode === 'single' && (
                <Alert severity={result.status === 'PendingApproval' ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    {result.status === 'PendingApproval'
                        ? 'Quantidade acima do limite de auto-aprovação — solicitação enviada para aprovação.'
                        : `Concedido com sucesso. Novo saldo: ${result.newBalance} 🍋.`}
                </Alert>
            )}
            {result && mode === 'batch' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {result.results.filter((r) => r.success).length} de {result.results.length} concessões processadas
                    {result.results.some((r) => !r.success) && ' — verifique os erros abaixo.'}
                    <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                        {result.results.map((line, i) => (
                            <li key={i} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                {line.consultantId}: {line.success ? (line.transactionId ? 'concedido' : 'pendente de aprovação') : `erro — ${line.error}`}
                            </li>
                        ))}
                    </Box>
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                    select
                    label="Motivo"
                    size="small"
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    {activeReasons.length === 0 && <MenuItem value="" disabled>Nenhum motivo ativo cadastrado</MenuItem>}
                    {activeReasons.map((r) => (
                        <MenuItem key={r.id} value={r.code}>{r.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Data de referência"
                    type="date"
                    size="small"
                    value={referenceDate}
                    onChange={(e) => setReferenceDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <FormControlLabel
                    control={<Checkbox checked={notifyConsultant} onChange={(e) => setNotifyConsultant(e.target.checked)} />}
                    label="Notificar consultor"
                />
            </Box>

            <TextField
                label="Justificativa"
                fullWidth
                multiline
                minRows={2}
                size="small"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                helperText="Mínimo de 10 caracteres — vira a descrição de todas as concessões deste envio."
                sx={{ mb: 2 }}
            />

            {mode === 'single' ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <ToggleButtonGroup
                        value={personRole}
                        exclusive
                        onChange={handlePersonRoleChange}
                        size="small"
                        disabled={isSubmitting}
                    >
                        <ToggleButton value="Consultant">Consultor</ToggleButton>
                        <ToggleButton value="Talent">Talento</ToggleButton>
                    </ToggleButtonGroup>
                    <Box sx={{ minWidth: 260 }}>
                        <PersonAutocomplete
                            value={consultant}
                            onChange={setConsultant}
                            disabled={isSubmitting}
                            role={personRole}
                            label={ROLE_LABELS[personRole]}
                        />
                    </Box>
                    <TextField
                        label="Quantidade"
                        type="number"
                        size="small"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        sx={{ width: 150 }}
                    />
                    <Button
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        style={{ backgroundColor: '#f77513', color: 'white' }}
                        onClick={handleSubmitSingle}
                    >
                        Conceder
                    </Button>
                </Box>
            ) : (
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Concessão em lote disponível apenas para consultores.
                    </Typography>
                    {batchRows.map((row) => (
                        <Box key={row.key} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 240 }}>
                                <PersonAutocomplete
                                    value={row.consultant}
                                    onChange={(value) => handleBatchRowChange(row.key, 'consultant', value)}
                                    disabled={isSubmitting}
                                    role="Consultant"
                                    label="Consultor"
                                />
                            </Box>
                            <TextField
                                label="Quantidade"
                                type="number"
                                size="small"
                                value={row.quantity}
                                onChange={(e) => handleBatchRowChange(row.key, 'quantity', e.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                sx={{ width: 130 }}
                            />
                            <TextField
                                label="Descrição (opcional, sobrepõe a justificativa acima)"
                                size="small"
                                value={row.description}
                                onChange={(e) => handleBatchRowChange(row.key, 'description', e.target.value)}
                                sx={{ minWidth: 260, flexGrow: 1 }}
                            />
                            <IconButton
                                size="small"
                                disabled={batchRows.length === 1}
                                onClick={() => handleRemoveBatchRow(row.key)}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={handleAddBatchRow}>
                            Adicionar linha
                        </Button>
                        <Button
                            variant="contained"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                            style={{ backgroundColor: '#f77513', color: 'white' }}
                            onClick={handleSubmitBatch}
                        >
                            Conceder em lote
                        </Button>
                    </Box>
                </Box>
            )}

            <CoinGrantPendingPanel ref={pendingPanelRef} />
        </Paper>
    );
};

export default CoinGrantsPanel;
